import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError } from "@/components/ui/Toast";
import { useDeleteAsset, useEventAssets, usePatchAsset } from "@/api/hooks/useProof";
import {
  ASSET_KIND_LABELS,
  POST_PERMISSION_BADGE_VARIANT,
  POST_PERMISSION_LABELS,
  PostPermissionSchema,
  type Asset,
  type PostPermission,
} from "@/api/types/asset";
import { AddAssetDialog } from "./AddAssetDialog";

const POST_PERMISSIONS = PostPermissionSchema.options;

interface EventAssetsCardProps {
  eventId: string;
}

/**
 * The event's media assets — hero photo, reveal clip, reaction clip,
 * NA-parity shot, gallery, recap reel. Each row tracks whether the file's
 * been delivered to the host and the playbook's "get explicit yes,
 * screenshot it" posting-permission state as an enum + note field.
 */
export function EventAssetsCard({ eventId }: EventAssetsCardProps) {
  const { data: assets } = useEventAssets(eventId);
  const [addOpen, setAddOpen] = useState(false);
  const list = assets ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Assets</CardTitle>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          Add asset
        </Button>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p className="text-sm text-text-muted" data-testid="assets-empty">
            No assets logged yet.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="asset-list">
            {list.map((asset) => (
              <AssetRow key={asset.id} eventId={eventId} asset={asset} />
            ))}
          </ul>
        )}
      </CardContent>

      <AddAssetDialog open={addOpen} onClose={() => setAddOpen(false)} eventId={eventId} />
    </Card>
  );
}

function AssetRow({ eventId, asset }: { eventId: string; asset: Asset }) {
  const patchAsset = usePatchAsset();
  const deleteAsset = useDeleteAsset();

  async function toggleDelivered(checked: boolean) {
    try {
      await patchAsset.mutateAsync({ eventId, assetId: asset.id, delivered_to_host: checked });
    } catch (err) {
      toastError("Couldn't update delivery status", getErrorMessage(err));
    }
  }

  async function setPermission(value: PostPermission) {
    try {
      await patchAsset.mutateAsync({ eventId, assetId: asset.id, post_permission: value });
    } catch (err) {
      toastError("Couldn't update posting permission", getErrorMessage(err));
    }
  }

  async function remove() {
    try {
      await deleteAsset.mutateAsync({ eventId, assetId: asset.id });
    } catch (err) {
      toastError("Couldn't delete asset", getErrorMessage(err));
    }
  }

  return (
    <li
      className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-asset-id={asset.id}
    >
      <div className="min-w-0">
        <p className="font-medium text-text-primary">
          {ASSET_KIND_LABELS[asset.kind]}
          <Badge variant={POST_PERMISSION_BADGE_VARIANT[asset.post_permission]} size="sm" className="ml-2">
            {POST_PERMISSION_LABELS[asset.post_permission]}
          </Badge>
        </p>
        <p className="truncate text-xs text-text-muted">{asset.r2_key}</p>
        {asset.caption && <p className="text-xs text-text-secondary">{asset.caption}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Checkbox
          label="Delivered to host"
          checked={asset.delivered_to_host}
          disabled={patchAsset.isPending}
          onChange={(e) => void toggleDelivered(e.target.checked)}
        />
        <select
          aria-label={`Posting permission for ${ASSET_KIND_LABELS[asset.kind]}`}
          value={asset.post_permission}
          disabled={patchAsset.isPending}
          onChange={(e) => void setPermission(e.target.value as PostPermission)}
          className="h-8 rounded-md border border-border bg-bg-card px-2 text-xs"
        >
          {POST_PERMISSIONS.map((p) => (
            <option key={p} value={p}>
              {POST_PERMISSION_LABELS[p]}
            </option>
          ))}
        </select>
        <Button
          variant="ghost-danger"
          size="sm"
          disabled={deleteAsset.isPending}
          onClick={() => void remove()}
        >
          Delete
        </Button>
      </div>
    </li>
  );
}
